import React, { useState, useEffect } from "react";
import { 
  create_event_category,
  update_event_category,
  create_event,
  update_event
} from "../../../Apiservices/Auth/servers/fetchProviderData";
import "./EventsTable.css";
import { fetchAllEventCategoriesWithEvents } from "../../../Apiservices/Auth/servers/fetchCategoryEvents";

const EventsTable = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRowId, setEditRowId] = useState(null);
  const [expandedRows, setExpandedRows] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // State for creating new category
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({
    name: "",
    description: "",
    isActive: true,
    events: []
  });
  
  // State for creating new event under category
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [newEventData, setNewEventData] = useState({
    name: "",
    description: "",
    isActive: true
  });
  
  // State for editing event
  const [editingEvent, setEditingEvent] = useState(null);
  const [showEditEvent, setShowEditEvent] = useState(false);

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Function to fetch events
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const eventsData = await fetchAllEventCategoriesWithEvents();
      setEvents(eventsData || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit form input changes
  const handleInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle new category input changes
  const handleNewCategoryChange = (field, value) => {
    setNewCategoryData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle new event input changes
  const handleNewEventChange = (field, value) => {
    setNewEventData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Start editing a row
  const handleEditClick = (rowData) => {
    setEditRowId(rowData.id);
    setEditFormData({
      name: rowData.name,
      description: rowData.description || "",
      isActive: rowData.isActive !== undefined ? rowData.isActive : true
    });
  };

  // Handle update submission for category
  // In your handleUpdateSubmit function
const handleUpdateSubmit = async (id) => {
  if (!editFormData.name?.trim()) {
    alert("Category name is required");
    return;
  }

  setIsSubmitting(true);
  try {
    const updateData = {
      name: editFormData.name,
      description: editFormData.description || null,
      imageUrl: editFormData.imageUrl || null, // Add this
      isActive: editFormData.isActive,
    };

    console.log("📤 [FRONTEND] Sending update category data:", JSON.stringify(updateData, null, 2));
    
    const response = await update_event_category(updateData, id);
    
    if (response) {
      setEditRowId(null);
      setEditFormData({});
      await fetchEvents();
      alert("Category updated successfully!");
    }
  } catch (error) {
    console.error("❌ Error updating category:", error);
    console.error("❌ Error response:", error.response?.data);
    alert("Failed to update category");
  } finally {
    setIsSubmitting(false);
  }
};

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditRowId(null);
    setEditFormData({});
  };

  // Handle delete category (soft delete by setting isActive to false)
  const handleDeleteCategory = async (categoryId) => {
  // Find the current category to check its status
  const category = events.find(cat => cat.id === categoryId);
  if (!category) return;

  const action = category.isActive ? 'deactivate' : 'activate';
  const actionText = category.isActive ? 'Deactivate' : 'Activate';
  
  if (window.confirm(`Are you sure you want to ${action} this category?`)) {
    try {
      setIsSubmitting(true);
      const updateData = {
        isActive: !category.isActive // Toggle the status
      };
      
      await update_event_category(updateData, categoryId);
      await fetchEvents();
      alert(`Category ${actionText}d successfully!`);
    } catch (error) {
      console.error(`Error ${action}ing category:`, error);
      alert(`Failed to ${action} category`);
    } finally {
      setIsSubmitting(false);
    }
  }
};

  // Handle delete event (soft delete by setting is_active to false)
  const handleDeleteEvent = async (categoryId, eventId) => {
    if (window.confirm("Are you sure you want to delete this event? This will make it inactive.")) {
      try {
        setIsSubmitting(true);
        const updateData = {
          is_active: false
        };
        
        await update_event(updateData, eventId);
        await fetchEvents();
        alert("Event deactivated successfully!");
      } catch (error) {
        console.error("Error deleting event:", error);
        alert("Failed to delete event");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Handle create new category  
const handleCreateCategory = async () => {
  if (!newCategoryData.name.trim()) {
    alert("Category name is required");
    return;
  }

  setIsSubmitting(true);
  try {
    // Prepare data for bulk create API - send exactly what backend expects
    const createData = {
      categories: [{
        name: newCategoryData.name,
        description: newCategoryData.description || null,
        imageUrl: newCategoryData.imageUrl || null, // Add this if your model has it
        isActive: newCategoryData.isActive,
        events: newCategoryData.events.map(event => ({
          name: event.name,
          description: event.description || null,
          imageUrl: event.imageUrl || null, // Add this if your model has it
          isActive: event.isActive
          // DO NOT send eventCategoryId or event_category_id here
          // Backend will set it automatically after creating the category
        }))
      }]
    };

    console.log("📤 [FRONTEND] Sending create data:", JSON.stringify(createData, null, 2));
    
    const response = await create_event_category(createData);
    
    if (response) {
      console.log("✅ Category creation successful:", response);
      setShowCreateCategory(false);
      setNewCategoryData({
        name: "",
        description: "",
        imageUrl: "", // Add this
        isActive: true,
        events: []
      });
      await fetchEvents();
      alert("Category created successfully!");
    }
  } catch (error) {
    console.error("❌ Error creating category:", error);
    console.error("❌ Error response:", error.response?.data);
    alert("Failed to create category: " + (error.response?.data?.error || error.message));
  } finally {
    setIsSubmitting(false);
  }
};

  // Handle open create event dialog
  const handleOpenCreateEvent = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setNewEventData({
      name: "",
      description: "",
      isActive: true
    });
    setShowCreateEvent(true);
  };

  // Handle create new event under category
 
const handleCreateEvent = async () => {
  if (!newEventData.name.trim()) {
    alert("Event name is required");
    return;
  }

  setIsSubmitting(true);
  try {
    // Prepare data for event creation - send exactly what backend expects
    const createData = {
      eventCategories: [{
        eventCategoryId: selectedCategoryId, // camelCase as per your example
        events: [{
          name: newEventData.name,
          description: newEventData.description || null,
          imageUrl: newEventData.imageUrl || null, // Add this
          isActive: newEventData.isActive
        }]
      }]
    };

    console.log("📤 [FRONTEND] Sending create event data:", JSON.stringify(createData, null, 2));
    
    const response = await create_event(createData);
    
    if (response) {
      console.log("✅ Event creation successful:", response);
      setShowCreateEvent(false);
      setNewEventData({
        name: "",
        description: "",
        imageUrl: "", // Add this
        isActive: true
      });
      await fetchEvents();
      alert("Event created successfully!");
    }
  } catch (error) {
    console.error("❌ Error creating event:", error);
    console.error("❌ Error response:", error.response?.data);
    alert("Failed to create event: " + (error.response?.data?.error || error.message));
  } finally {
    setIsSubmitting(false);
  }
};

  // Handle edit event
  const handleEditEvent = (eventData) => {
    setEditingEvent(eventData);
    setNewEventData({
      name: eventData.name,
      description: eventData.description || "",
      isActive: eventData.is_active !== undefined ? eventData.is_active : true
    });
    setShowEditEvent(true);
  };

  // Handle update event
  
const handleUpdateEvent = async () => {
  if (!newEventData.name.trim()) {
    alert("Event name is required");
    return;
  }

  setIsSubmitting(true);
  try {
    const updateData = {
      name: newEventData.name,
      description: newEventData.description || null,
      imageUrl: newEventData.imageUrl || null, // Add this
      is_active: newEventData.isActive, // Use underscore to match model
    };

    console.log("📤 [FRONTEND] Sending update event data:", JSON.stringify(updateData, null, 2));
    
    const response = await update_event(updateData, editingEvent.id);
    
    if (response) {
      setShowEditEvent(false);
      setEditingEvent(null);
      setNewEventData({
        name: "",
        description: "",
        imageUrl: "", // Add this
        isActive: true
      });
      await fetchEvents();
      alert("Event updated successfully!");
    }
  } catch (error) {
    console.error("❌ Error updating event:", error);
    console.error("❌ Error response:", error.response?.data);
    alert("Failed to update event");
  } finally {
    setIsSubmitting(false);
  }
};

  // Add event to new category (for bulk creation)
  const addEventToNewCategory = () => {
    setNewCategoryData(prev => ({
      ...prev,
      events: [...prev.events, {
        name: "",
        description: "",
        isActive: true
      }]
    }));
  };

  // Handle event change in new category
  const handleNewCategoryEventChange = (index, field, value) => {
    const updatedEvents = [...newCategoryData.events];
    updatedEvents[index] = {
      ...updatedEvents[index],
      [field]: value
    };
    setNewCategoryData(prev => ({
      ...prev,
      events: updatedEvents
    }));
  };

  // Remove event from new category
  const removeEventFromNewCategory = (index) => {
    const updatedEvents = newCategoryData.events.filter((_, i) => i !== index);
    setNewCategoryData(prev => ({
      ...prev,
      events: updatedEvents
    }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render loading state
  if (loading) {
    return (
      <div className="events-table-container">
        <div className="loading-state">Loading events...</div>
      </div>
    );
  }

  return (
    <div className="events-management-container">
      {/* Header with Create Button */}
      <div className="events-header">
        <h1>Event Categories Management</h1>
        <button 
          className="btn-create-category"
          onClick={() => setShowCreateCategory(true)}
          disabled={isSubmitting}
        >
          + Create New Category
        </button>
      </div>

      {/* Render the main table */}
      <div className="events-table-container">
        <div className="table-responsive">
          <table className="events-table">
            <thead>
              <tr>
                <th style={{ width: "50px" }}></th>
                <th style={{ width: "80px" }}>S. No</th>
                <th>Category Name</th>
                <th>Status</th>
                <th>Last Updated</th>
                <th style={{ width: "200px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event, index) => (
                <React.Fragment key={event.id}>
                  <tr className={expandedRows?.[event.id] ? "expanded" : ""}>
                    <td>
                      <button 
                        className="expand-btn"
                        onClick={() => {
                          const newExpandedRows = { ...expandedRows };
                          if (newExpandedRows[event.id]) {
                            delete newExpandedRows[event.id];
                          } else {
                            newExpandedRows[event.id] = true;
                          }
                          setExpandedRows(newExpandedRows);
                        }}
                      >
                        {expandedRows?.[event.id] ? "−" : "+"}
                      </button>
                    </td>
                    <td>{index + 1}</td>
                    <td>
                      {editRowId === event.id ? (
                        <input
                          type="text"
                          className="edit-input"
                          value={editFormData.name || ""}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          placeholder="Category name"
                        />
                      ) : (
                        <div>
                          {event.name}
                          {!event.isActive && (
                            <span className="inactive-badge">Inactive</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${event.isActive ? 'active' : 'inactive'}`}>
                        {event.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {formatDate(event.updatedAt)}
                    </td>
                    <td>
                      {editRowId === event.id ? (
                        <div className="edit-actions">
                          <button 
                            className="btn-save"
                            onClick={() => handleUpdateSubmit(event.id)}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Saving...' : 'Save'}
                          </button>
                          <button 
                            className="btn-cancel"
                            onClick={handleCancelEdit}
                            disabled={isSubmitting}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="table-actions">
                          <button 
                            className="btn-edit"
                            onClick={() => handleEditClick(event)}
                            disabled={isSubmitting || !event.isActive}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn-delete"
                            onClick={() => handleDeleteCategory(event.id)}
                            disabled={isSubmitting}
                          >
                            {event.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          {event.isActive && (
                            <button 
                              className="btn-add-event"
                              onClick={() => handleOpenCreateEvent(event.id)}
                              disabled={isSubmitting}
                            >
                              + Add Event
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  {expandedRows?.[event.id] && (
                    <tr className="expansion-row">
                      <td colSpan="6">
                        {editRowId === event.id ? (
                          <div className="edit-form">
                            <div className="form-group">
                              <label>Description:</label>
                              <textarea
                                className="edit-textarea"
                                value={editFormData.description || ""}
                                onChange={(e) => handleInputChange("description", e.target.value)}
                                placeholder="Category description"
                                rows="3"
                              />
                            </div>
                            <div className="form-group">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={editFormData.isActive}
                                  onChange={(e) => handleInputChange("isActive", e.target.checked)}
                                />
                                {" "}Active
                              </label>
                            </div>
                          </div>
                        ) : (
                          <div className="sub-events-section">
                            <div className="sub-events-header">
                              <h4>Events in "{event.name}"</h4>
                              {event.isActive && (
                                <button 
                                  className="btn-add-event-small"
                                  onClick={() => handleOpenCreateEvent(event.id)}
                                  disabled={isSubmitting}
                                >
                                  + Add New Event
                                </button>
                              )}
                            </div>
                            
                            {event.events && event.events.length > 0 ? (
                              <table className="nested-table">
                                <thead>
                                  <tr>
                                    <th>Event Name</th>
                                    <th>Description</th>
                                    <th>Status</th>
                                    <th>Last Updated</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {event.events.filter(e => e.is_active !== false).map((subEvent) => (
                                    <tr key={subEvent.id} className={!subEvent.is_active ? 'inactive-row' : ''}>
                                      <td>
                                        {subEvent.name}
                                        {!subEvent.is_active && (
                                          <span className="inactive-badge">Inactive</span>
                                        )}
                                      </td>
                                      <td>{subEvent.description || '-'}</td>
                                      <td>
                                        <span className={`status-badge ${subEvent.is_active ? 'active' : 'inactive'}`}>
                                          {subEvent.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                      </td>
                                      <td>{formatDate(subEvent.updatedAt)}</td>
                                      <td>
                                        <div className="table-actions">
                                          <button 
                                            className="btn-edit btn-sm"
                                            onClick={() => handleEditEvent(subEvent)}
                                            disabled={isSubmitting || !subEvent.is_active}
                                          >
                                            Edit
                                          </button>
                                          <button 
                                            className="btn-delete btn-sm"
                                            onClick={() => handleDeleteEvent(event.id, subEvent.id)}
                                            disabled={isSubmitting}
                                          >
                                            {subEvent.is_active ? 'Deactivate' : 'Deleted'}
                                          </button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div className="no-events">
                                <p>No events in this category</p>
                                {event.isActive && (
                                  <button 
                                    className="btn-add-event"
                                    onClick={() => handleOpenCreateEvent(event.id)}
                                    disabled={isSubmitting}
                                  >
                                    + Add First Event
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Category Dialog */}
      {showCreateCategory && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Create New Event Category</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowCreateCategory(false);
                  setNewCategoryData({
                    name: "",
                    description: "",
                    isActive: true,
                    events: []
                  });
                }}
                disabled={isSubmitting}
              >
                &times;
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={newCategoryData.name}
                  onChange={(e) => handleNewCategoryChange("name", e.target.value)}
                  placeholder="Enter category name"
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newCategoryData.description}
                  onChange={(e) => handleNewCategoryChange("description", e.target.value)}
                  placeholder="Enter description"
                  rows="3"
                  className="form-textarea"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newCategoryData.isActive}
                    onChange={(e) => handleNewCategoryChange("isActive", e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span>Active</span>
                </label>
              </div>

              {/* Events for bulk creation */}
              <div className="events-section">
                <div className="section-header">
                  <h4>Add Events (Optional)</h4>
                  <button 
                    type="button"
                    className="btn-add-event-small"
                    onClick={addEventToNewCategory}
                    disabled={isSubmitting}
                  >
                    + Add Event
                  </button>
                </div>
                
                {newCategoryData.events.map((event, index) => (
                  <div key={index} className="event-form-row">
                    <div className="form-group">
                      <label>Event Name</label>
                      <input
                        type="text"
                        value={event.name}
                        onChange={(e) => handleNewCategoryEventChange(index, "name", e.target.value)}
                        placeholder="Enter event name"
                        className="form-input"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={event.description}
                        onChange={(e) => handleNewCategoryEventChange(index, "description", e.target.value)}
                        placeholder="Enter event description"
                        rows="2"
                        className="form-textarea"
                        disabled={isSubmitting}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeEventFromNewCategory(index)}
                      disabled={isSubmitting}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowCreateCategory(false);
                  setNewCategoryData({
                    name: "",
                    description: "",
                    isActive: true,
                    events: []
                  });
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={handleCreateCategory}
                disabled={isSubmitting || !newCategoryData.name.trim()}
              >
                {isSubmitting ? 'Creating...' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Dialog */}
      {showCreateEvent && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Add New Event</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowCreateEvent(false);
                  setNewEventData({
                    name: "",
                    description: "",
                    isActive: true
                  });
                }}
                disabled={isSubmitting}
              >
                &times;
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Event Name *</label>
                <input
                  type="text"
                  value={newEventData.name}
                  onChange={(e) => handleNewEventChange("name", e.target.value)}
                  placeholder="Enter event name"
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newEventData.description}
                  onChange={(e) => handleNewEventChange("description", e.target.value)}
                  placeholder="Enter event description"
                  rows="3"
                  className="form-textarea"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newEventData.isActive}
                    onChange={(e) => handleNewEventChange("isActive", e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowCreateEvent(false);
                  setNewEventData({
                    name: "",
                    description: "",
                    isActive: true
                  });
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={handleCreateEvent}
                disabled={isSubmitting || !newEventData.name.trim()}
              >
                {isSubmitting ? 'Creating...' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Dialog */}
      {showEditEvent && editingEvent && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Edit Event</h2>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowEditEvent(false);
                  setEditingEvent(null);
                  setNewEventData({
                    name: "",
                    description: "",
                    isActive: true
                  });
                }}
                disabled={isSubmitting}
              >
                &times;
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Event Name *</label>
                <input
                  type="text"
                  value={newEventData.name}
                  onChange={(e) => handleNewEventChange("name", e.target.value)}
                  placeholder="Enter event name"
                  className="form-input"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newEventData.description}
                  onChange={(e) => handleNewEventChange("description", e.target.value)}
                  placeholder="Enter event description"
                  rows="3"
                  className="form-textarea"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newEventData.isActive}
                    onChange={(e) => handleNewEventChange("isActive", e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span>Active</span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowEditEvent(false);
                  setEditingEvent(null);
                  setNewEventData({
                    name: "",
                    description: "",
                    isActive: true
                  });
                }}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={handleUpdateEvent}
                disabled={isSubmitting || !newEventData.name.trim()}
              >
                {isSubmitting ? 'Updating...' : 'Update Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsTable;